import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { HttpService } from '../../utility/http-service';
import { GridApiService } from '../../components/wc-grid/services/grid-api.service';
import { LookupOption } from '../../components/wc-grid/grid-types';

export interface ForeignKeyCreateDialogData {
  schemaId: string;
}

@Component({
  selector: 'app-foreign-key-create-dialog',
  standalone: true,
  templateUrl: './foreign-key-create-dialog.component.html',
  styleUrls: ['./foreign-key-create-dialog.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
  ],
})
export class ForeignKeyCreateDialogComponent implements OnInit {
  form!: FormGroup;
  saving = false;
  error: string | null = null;

  tableOptions: LookupOption[] = [];
  sourceFieldOptions: LookupOption[] = [];
  targetFieldOptions: LookupOption[] = [];

  // Free-text boxes that drive the searchable Source/Target Table autocompletes. Kept separate
  // from the form's sourceTableId/targetTableId controls (which hold the actual table id) —
  // each holds a plain string while the user is typing/filtering, and the selected LookupOption
  // object once they pick a suggestion (see displayTableOption/onTableSelected below).
  sourceTableSearch = new FormControl<LookupOption | string>('', Validators.required);
  targetTableSearch = new FormControl<LookupOption | string>('', Validators.required);
  filteredSourceTableOptions: LookupOption[] = [];
  filteredTargetTableOptions: LookupOption[] = [];

  constructor(
    private fb: FormBuilder,
    private http: HttpService,
    private gridApi: GridApiService,
    public dialogRef: MatDialogRef<ForeignKeyCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ForeignKeyCreateDialogData,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      sourceTableId: ['', Validators.required],
      targetTableId: ['', Validators.required],
      keyAlias: ['', Validators.required],
      displayName: ['', Validators.required],
      displayNamePlural: ['', Validators.required],
      fieldPairs: this.fb.array([this.newPair()]),
    });

    this.gridApi.lookup(
      'modelsTables',
      'tableOptions',
      { filter: { logic: 'AND', rules: [{ field: 'SchemaId', op: 'Eq', value: this.data.schemaId }] }, take: 200 },
      (opts: LookupOption[]) => {
        this.tableOptions = opts;
        this.filteredSourceTableOptions = this.filterTables(this.sourceTableSearch.value);
        this.filteredTargetTableOptions = this.filterTables(this.targetTableSearch.value);
      },
    );

    this.sourceTableSearch.valueChanges.subscribe((v) => this.onTableSearchChange(v, 'source'));
    this.targetTableSearch.valueChanges.subscribe((v) => this.onTableSearchChange(v, 'target'));

    this.form.get('sourceTableId')!.valueChanges.subscribe((tableId: string) => this.loadGuidFields(tableId, 'source'));
    this.form.get('targetTableId')!.valueChanges.subscribe((tableId: string) => this.loadGuidFields(tableId, 'target'));
  }

  private filterTables(value: LookupOption | string | null): LookupOption[] {
    const term = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (!term) return this.tableOptions;
    return this.tableOptions.filter((o) => o.label.toLowerCase().includes(term));
  }

  /** Clears the real sourceTableId/targetTableId control while the user is still typing/filtering
   *  (an unconfirmed string), and sets it once they've actually picked a suggestion (an object). */
  private onTableSearchChange(value: LookupOption | string | null, which: 'source' | 'target'): void {
    const idControl = this.form.get(which === 'source' ? 'sourceTableId' : 'targetTableId')!;
    if (which === 'source') this.filteredSourceTableOptions = this.filterTables(value);
    else this.filteredTargetTableOptions = this.filterTables(value);

    idControl.setValue(value && typeof value === 'object' ? value.value : '');
  }

  onTableSelected(event: MatAutocompleteSelectedEvent, which: 'source' | 'target'): void {
    // No-op beyond what the valueChanges subscription above already does — kept for clarity/
    // future use (e.g. if we ever need to react specifically to a confirmed pick vs. typing).
  }

  displayTableOption(o: LookupOption | string | null): string {
    if (!o) return '';
    return typeof o === 'string' ? o : o.label;
  }

  private loadGuidFields(tableId: string, which: 'source' | 'target'): void {
    if (!tableId) {
      if (which === 'source') this.sourceFieldOptions = [];
      else this.targetFieldOptions = [];
      return;
    }

    this.gridApi.lookup(
      'modelsFields',
      'guidFields',
      { filter: { logic: 'AND', rules: [{ field: 'TableId', op: 'Eq', value: tableId }] }, take: 500 },
      (opts: LookupOption[]) => {
        if (which === 'source') this.sourceFieldOptions = opts;
        else this.targetFieldOptions = opts;
      },
    );
  }

  get fieldPairs(): FormArray {
    return this.form.get('fieldPairs') as FormArray;
  }

  private newPair(): FormGroup {
    return this.fb.group({
      sourceFieldId: ['', Validators.required],
      targetFieldId: ['', Validators.required],
    });
  }

  addPair(): void {
    this.fieldPairs.push(this.newPair());
  }

  removePair(i: number): void {
    if (this.fieldPairs.length <= 1) return;
    this.fieldPairs.removeAt(i);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.sourceTableSearch.markAsTouched();
      this.targetTableSearch.markAsTouched();
      return;
    }

    this.saving = true;
    this.error = null;

    const val = this.form.value;
    const body = {
      SchemaId: this.data.schemaId,
      SourceTableId: val.sourceTableId,
      TargetTableId: val.targetTableId,
      KeyAlias: val.keyAlias,
      DisplayName: val.displayName,
      DisplayNamePlural: val.displayNamePlural,
      FieldPairs: val.fieldPairs.map((p: any) => ({ SourceFieldId: p.sourceFieldId, TargetFieldId: p.targetFieldId })),
    };

    this.http.postReturn(
      'Models/ForeignKeys',
      () => this.dialogRef.close(true),
      undefined,
      body,
      () => { this.saving = false; },
    );
  }
}
